import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TSortColumn, TSortDirection } from '../../classes/grid-page-info';
import {
  getClaimFilingIndicatorsQuery,
  getClaimsArchiveQuery,
  getClaimsCountsQuery,
  getClaimsErrorsQuery,
  getClaimsInputFileQuery,
  getClaimsInputQuery,
  getClaimsOutputQuery,
  getClaimsQuery,
  getClaimsRemittanceQuery,
  getClaimStatusDetailsQuery,
  getClaimsTransmitFileQuery,
} from '../../interfaces/claim/claim.graphql';
import {
  IClaim,
  IClaimError,
  IClaimFilingIndicator,
  IClaimInput,
  IClaimInputFile,
  IClaimOutput,
  IClaimsRemittance,
  IClaimsRemittanceParams,
  IClaimStatusDetails,
  IGQLClaimFilingIndicatorParams,
  IGQLClaimsErrorParams,
  IGQLClaimsInputFileParams,
  IGQLClaimsParams,
} from '../../interfaces/claim/claim.interface';
import {
  GQLArguments,
  GQLResponse,
  IGQLResponse,
  IGQLSingleResponse,
} from '../../interfaces/graphql.interface';
import { HelperGQLService } from '../helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class ClaimsGQLService {
  constructor(
    private apollo: Apollo,
    private gqlService: HelperGQLService,
  ) {}

  getClaimsCount(
    claimsParams?: IGQLClaimsParams,
  ): Observable<GQLResponse<IClaim>> {
    const { orderby, direction, filter, after } = claimsParams ?? {};
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const gqlArguments: GQLArguments = {
      first: 1_000_000,
    };

    if (filter) {
      const claimsFilters: Record<string, unknown> = {
        claimStatus: { eq: filter.claimStatus },
        claimId: { eq: filter.claimId },
        patientAccountNumber: { eq: filter.patientAccountNumber },
        patientName: { contains: filter.patientName },
        reportId: { eq: filter.reportId },
        billingNpi: { eq: filter.billingNpi },
        renderingNpi: { eq: filter.renderingNpi },
        processorId: { eq: filter.processorId },
        neicId: { eq: filter.neicId },
        formatType: { eq: filter.formatType },
        billingTaxId: { eq: filter.billingTaxId },
        ...this.getDateFilters({
          claimsDate: filter.claimsDate ?? new Date().toISOString(),
          showLast: filter.showLast,
          dateFrom: filter.dateFrom,
          dateTo: filter.dateTo,
        }),
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IClaim>>({
        query: getClaimsCountsQuery,
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claims;
        }),
      );
  }

  getClaimsArchive(
    claimsParams?: IGQLClaimsParams,
  ): Observable<GQLResponse<IClaim>> {
    const { orderby, direction, filter, after } = claimsParams ?? {};
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const gqlArguments: GQLArguments = {
      first: 30,
    };

    if (filter) {
      const claimsFilters: Record<string, unknown> = {
        claimStatus: { eq: filter.claimStatus },
        claimId: { eq: filter.claimId },
        patientAccountNumber: { eq: filter.patientAccountNumber },
        patientName: { contains: filter.patientName },
        reportId: { eq: filter.reportId },
        billingNpi: { eq: filter.billingNpi },
        renderingNpi: { eq: filter.renderingNpi },
        processorId: { eq: filter.processorId },
        neicId: { eq: filter.neicId },
        formatType: { eq: filter.formatType },
        billingTaxId: { eq: filter.billingTaxId },
        ...this.getDateFilters({
          claimsDate: filter.claimsDate ?? new Date().toISOString(),
          showLast: filter.showLast,
          dateFrom: filter.dateFrom,
          dateTo: filter.dateTo,
        }),
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IClaim>>({
        query: getClaimsArchiveQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimsArchive;
        }),
      );
  }

  getClaims(claimsParams?: IGQLClaimsParams): Observable<GQLResponse<IClaim>> {
    const { orderby, direction, filter, after } = claimsParams ?? {};
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const or = (x) => {
      return x?.length > 1
        ? {
            or: x.map((x) => {
              return { eq: x };
            }),
          }
        : { eq: x ? x[0] : null };
    };

    const gqlArguments: GQLArguments = {
      first: 30,
    };

    if (filter) {
      const claimsFilters: Record<string, unknown> = {
        claimStatus: or(filter.claimStatus),
        claimId: { eq: filter.claimId },
        patientAccountNumber: { eq: filter.patientAccountNumber },
        patientName: { contains: filter.patientName },
        reportId: { eq: filter.reportId },
        billingNpi: { eq: filter.billingNpi },
        renderingNpi: { eq: filter.renderingNpi },
        processorId: { eq: filter.processorId },
        neicId: { eq: filter.neicId },
        formatType: { eq: filter.formatType },
        billingTaxId: { eq: filter.billingTaxId },
        ...this.getDateFilters({
          claimsDate: filter.claimsDate ?? new Date().toISOString(),
          showLast: filter.showLast,
          dateFrom: filter.dateFrom,
          dateTo: filter.dateTo,
        }),
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IClaim>>({
        query: getClaimsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claims;
        }),
      );
  }

  getClaimsInput(claimID: string): Observable<IClaimInput> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IClaimInput>>({
        query: getClaimsInputQuery,
        fetchPolicy: 'no-cache',
        variables: {
          claimId: claimID,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimInputClaim;
        }),
      );
  }

  getClaimsOutput(claimID: string): Observable<IClaimOutput> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IClaimOutput>>({
        query: getClaimsOutputQuery,
        fetchPolicy: 'no-cache',
        variables: {
          claimId: claimID,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimSiblingById;
        }),
      );
  }

  getClaimsErrors(
    claimErrorsParams: IGQLClaimsErrorParams,
  ): Observable<GQLResponse<IClaimError>> {
    const { orderby, direction, filter, after, claimId } = claimErrorsParams;
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const gqlArguments: GQLArguments = {
      first: 300,
    };

    if (filter) {
      const claimsFilters: Record<string, unknown> = {
        providerId: { eq: filter.providerId },
        claimId: { eq: filter.claimId },
        errorId: { eq: filter.errorId },
        errorDate: { eq: filter.errorDate },
        lxCounter: { eq: filter.lxCounter },
        id: { eq: filter.id },
        messageText: { contains: filter.messageText },
        messageInfo: { contains: filter.messageInfo },
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IClaimError>>({
        query: getClaimsErrorsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
          claimId,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimErrors;
        }),
      );
  }

  getClaimInputFile(
    params: IGQLClaimsInputFileParams,
  ): Observable<IClaimInputFile> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IClaimInputFile>>({
        query: getClaimsInputFileQuery,
        fetchPolicy: 'no-cache',
        variables: {
          claimId: params.claimId,
          x_GROUP_ID: params.x_GROUP_ID,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimInputFile;
        }),
      );
  }

  getClaimTransmitFile(
    params: IGQLClaimsInputFileParams,
  ): Observable<IClaimInputFile> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IClaimInputFile>>({
        query: getClaimsTransmitFileQuery,
        fetchPolicy: 'no-cache',
        variables: {
          claimId: params.claimId,
          x_GROUP_ID: params.x_GROUP_ID,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimTransmitFile;
        }),
      );
  }

  getClaimsRemittance(
    claimErrorsParams: IClaimsRemittanceParams,
  ): Observable<GQLResponse<IClaimsRemittance>> {
    const { orderby, direction, filter, after, claimId } = claimErrorsParams;
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const gqlArguments: GQLArguments = {
      first: 300,
    };

    if (filter) {
      const claimsFilters: Record<string, unknown> = {
        actualFilename: { contains: filter.actualFilename },
        apicategory: { contains: filter.apicategory },
        carrier: { contains: filter.carrier },
        carrierProcessDate: { contains: filter.carrierProcessDate },
        carrierProcessTime: { contains: filter.carrierProcessTime },
        carrierTraceNumber: { contains: filter.carrierTraceNumber },
        checkNumber: { contains: filter.checkNumber },
        claimCharges: { contains: filter.claimCharges },
        claimId: { eq: filter.claimId },
        claimPaidAmount: { contains: filter.claimPaidAmount },
        claimStatus: { contains: filter.claimStatus },
        creationDate: { eq: filter.creationDate },
        csvFilename: { contains: filter.csvFilename },
        dateOfService: { contains: filter.dateOfService },
        description: { contains: filter.description },
        exportDate: { contains: filter.exportDate },
        fileName: { contains: filter.fileName },
        filename277ca: { contains: filter.filename277ca },
        id: { eq: filter.id },
        inputData: { contains: filter.inputData },
        npiOrTaxId: { contains: filter.npiOrTaxId },
        outputFilename: { contains: filter.outputFilename },
        patientAccountNumber: { contains: filter.patientAccountNumber },
        patientName: { contains: filter.patientName },
        payerId: { contains: filter.payerId },
        payerName: { contains: filter.payerName },
        payerTraceNumber: { contains: filter.payerTraceNumber },
        processFlag: { contains: filter.processFlag },
        providerGroupId: { contains: filter.providerGroupId },
        providerId: { contains: filter.providerId },
        reportName: { contains: filter.reportName },
        serviceProviderNPI: { contains: filter.serviceProviderNPI },
        xmlFilename: { contains: filter.xmlFilename },
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IClaimsRemittance>>({
        query: getClaimsRemittanceQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
          claimId,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimRemit;
        }),
      );
  }

  getClaimStatusDetails(claimID: string): Observable<IClaimStatusDetails> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IClaimStatusDetails>>({
        query: getClaimStatusDetailsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          claimId: claimID,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimStatusDetails;
        }),
      );
  }

  getClaimFilingIndicators(
    claimFilingIndicatorsParams?: IGQLClaimFilingIndicatorParams,
  ): Observable<GQLResponse<IClaimFilingIndicator>> {
    const { orderby, direction, filter, after } =
      claimFilingIndicatorsParams ?? {};
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const gqlArguments: GQLArguments = {
      first: 100,
    };

    if (filter) {
      const claimsFilters: Record<string, unknown> = {
        code: { eq: filter.code },
        description: { contains: filter.description },
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IClaimFilingIndicator>>({
        query: getClaimFilingIndicatorsQuery,
        fetchPolicy: 'cache-and-network',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.claimFilingIndicators;
        }),
      );
  }

  private getDateFilters(params: DateFiltersParams) {
    const data = {};

    if (!params.claimsDate) {
      return data;
    }

    data[params.claimsDate] = {
      gte: params.showLast
        ? params.showLast.startDate
        : this.formatDate(params.dateFrom ?? new Date().toISOString()),
      lte: params.showLast
        ? params.showLast.endDate
        : this.formatDate(params.dateTo ?? new Date().toISOString()),
    };

    return this.gqlService.cleanFilterObject(data);
  }

  private formatDate(date: string) {
    const pipe = new DatePipe('en-US');
    return `${pipe.transform(date, 'yyyy-MM-ddTHH:mm:ss')}Z`;
  }
}

type DateFiltersParams = {
  claimsDate: string;
  dateFrom?: string;
  dateTo?: string;
  showLast?: {
    endDate: string;
    startDate: string;
  };
};
